import React, { useState, useEffect } from 'react';
import '../styles/BinarySearchVisualizer.css'; // Import specific CSS

function BinarySearchVisualizer({ array, target }) {
  const [steps, setSteps] = useState([]);
  const [currentStep, setCurrentStep] = useState(0);
  const [message, setMessage] = useState('');
  const [mode, setMode] = useState('manual');
  const [intervalId, setIntervalId] = useState(null);

  useEffect(() => {
    if (array.length > 0 && target != null) {
      binarySearch(array, target);
    }
  }, [array, target]);

  useEffect(() => {
    if (mode === 'automatic') {
      const id = setInterval(() => {
        setCurrentStep((prevStep) => {
          if (prevStep < steps.length - 1) {
            return prevStep + 1;
          } else {
            clearInterval(id);
            return prevStep;
          }
        });
      }, 2000); // 2 seconds
      setIntervalId(id);
      return () => clearInterval(id);
    }
  }, [mode, steps]);

  const binarySearch = (arr, x) => {
    let start = 0;
    let end = arr.length - 1;
    const stepsArray = [];
    let foundIndex = -1;

    while (start <= end) {
      const mid = Math.floor((start + end) / 2);
      stepsArray.push({ start, mid, end, array: arr.slice(), status: `Checking middle element ${arr[mid]}` });

      if (arr[mid] === x) {
        foundIndex = mid;
        stepsArray.push({ start, mid: foundIndex, end, array: arr.slice(), status: `Element found at index ${foundIndex}` });
        break;
      } else if (arr[mid] < x) {
        start = mid + 1;
        stepsArray.push({ start, mid, end, array: arr.slice(), status: `Element ${x} is greater than ${arr[mid]}, searching right half` });
      } else {
        end = mid - 1;
        stepsArray.push({ start, mid, end, array: arr.slice(), status: `Element ${x} is less than ${arr[mid]}, searching left half` });
      }
    }

    if (foundIndex === -1) {
      stepsArray.push({ start: -1, mid: -1, end: -1, array: arr.slice(), status: 'Element not found' });
      setMessage('Element not found');
    }

    setSteps(stepsArray);
  };

  useEffect(() => {
    if (steps.length > 0 && currentStep === steps.length - 1) {
      setMessage(steps[currentStep].status); // Set the message based on the final step
    }
  }, [currentStep, steps]);

  const handleNextStep = () => {
    if (currentStep < steps.length - 1) {
      setCurrentStep(currentStep + 1);
    }
  };

  const handlePreviousStep = () => {
    if (currentStep > 0) {
      setCurrentStep(currentStep - 1);
    }
  };

  const handleModeToggle = () => {
    if (mode === 'manual') {
      setMode('automatic');
    } else {
      setMode('manual');
      clearInterval(intervalId);
    }
  };

  const currentArray = steps.length > 0 ? steps[currentStep].array : array;
  const { start, mid, end, status } = steps.length > 0 ? steps[currentStep] : { start: -1, mid: -1, end: -1, status: '' };

  return (
    <div>
      <h3>Visualization:</h3>
      <div className="array-container">
        {currentArray.map((value, index) => (
          <div
            key={index}
            className={`array-item ${index === mid ? 'current' : ''} ${value === target && index === mid ? 'found' : ''}`}
            style={{
              backgroundColor: index >= start && index <= end ? 'lightblue' : 'white',
              transform: index === mid ? 'scale(1.2)' : 'scale(1)',
              transition: 'all 0.3s ease',
            }}
          >
            {value}
          </div>
        ))}
      </div>
      <p className="message">{message}</p>
      <div className="step-details">
        <p>{status}</p>
      </div>
      <div className="controls">
        <button onClick={handlePreviousStep} disabled={currentStep === 0 || mode === 'automatic'}>
          Previous
        </button>
        <button onClick={handleNextStep} disabled={currentStep >= steps.length - 1 || mode === 'automatic'}>
          Next
        </button>
        <button onClick={handleModeToggle}>
          {mode === 'manual' ? 'Switch to Automatic' : 'Switch to Manual'}
        </button>
      </div>
      <div className="algorithm-explanation">
        <h4>Binary Search Algorithm Explanation</h4>
        <p>
          Binary Search is a highly efficient algorithm used to find an element in a sorted array. The basic idea is to reduce the problem size by half in each step. Here’s how it works:
        </p>
        <ol>
          <li>Start with two pointers, one pointing to the start of the array and the other pointing to the end.</li>
          <li>Find the middle element of the array.</li>
          <li>If the middle element is equal to the target element, the search is complete.</li>
          <li>If the target element is less than the middle element, repeat the search in the left half of the array.</li>
          <li>If the target element is greater than the middle element, repeat the search in the right half of the array.</li>
          <li>Continue the process until the target element is found or the search space is exhausted.</li>
        </ol>
        <p>
          Binary Search has a time complexity of O(log n), making it much faster than a linear search, especially for large datasets.
        </p>
      </div>
    </div>
  );
}

export default BinarySearchVisualizer;
